'use client'

import BaseLink, { BaseLinkObject } from '@/components/atoms/BaseLink/BaseLink'
import Button from '@/components/atoms/Button/Button'
import Logo from '@/components/atoms/Logo/Logo'
import { i18n, Locale } from '@/i18n'
import { cn } from '@/lib/cn'
import Link from 'next/link'
import BurgerButton from './BurgerButton'
import { useEffect, useId, useRef, useState } from 'react'
import Container from '@/components/atoms/Container/Container'
import { getFocusables } from '@/lib/getFocusables'

export type NavItem = BaseLinkObject & {
  children?: BaseLinkObject[]
}

type NavigationProps = {
  links: NavItem[]
  registerLink?: BaseLinkObject
  logInLink?: BaseLinkObject
  locale: Locale
  variant: 'static' | 'sticky'
  fixedFadeIn?: boolean
}

export default function Navigation({
  links,
  registerLink,
  logInLink,
  locale,
  variant,
  fixedFadeIn,
}: NavigationProps) {
  const [open, setOpen] = useState(false)
  const [hidden, setHidden] = useState(Boolean(fixedFadeIn && variant === 'sticky'))
  const id = useId().replaceAll(':', '')
  const menuButtonId = `menu-button-${id}`
  const menuPaneId = `menu-pane-${id}`

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      // Handle escape key
      if (event.code == 'Escape') {
        return setOpen(false)
      }

      // Handle tab key (focus trap)
      if (event.code == 'Tab') {
        // Assure target, container and menu button
        const target = (event.target as HTMLElement) || undefined
        const container =
          (document.querySelector(
            `#${menuPaneId}${window.innerWidth >= 768 ? ' > :first-child > :first-child' : ''}`,
          ) as HTMLElement) || undefined
        const menuButton =
          (document.querySelector(`#${menuButtonId}`) as HTMLButtonElement) || undefined
        if (!container || !target || !menuButton) return

        // Get focusable elements and currnet focus index
        const { focusables } = getFocusables({ container, currentFocus: target })
        const lastMenuItem = focusables[focusables.length - 1]
        if (!lastMenuItem) return

        // Trap focus to last menu item when tabbing backwards on #menuButtonId
        if (target?.id == menuButtonId && event.shiftKey) {
          if (lastMenuItem) {
            event.preventDefault()
            return lastMenuItem.focus()
          }
        }

        // Trap focus to menu button when tabbing forwards on last menu item
        if (target === lastMenuItem && !event.shiftKey) {
          if (menuButtonId) {
            event.preventDefault()
            return menuButton.focus()
          }
        }
      }
    }

    if (open) {
      const body = document.body
      body.classList.add('overflow-hidden')
      window.addEventListener('keydown', handleKeyDown)
    } else {
      const body = document.body
      body.classList.remove('overflow-hidden')
      window.removeEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, menuButtonId, menuPaneId])

  /** Handle fade in after x scroll */
  useEffect(() => {
    if (!fixedFadeIn || variant !== 'sticky') return
    function handleScroll() {
      if (window.scrollY > window.innerHeight / 6) {
        setHidden(false)
      } else {
        setHidden(true)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [fixedFadeIn, variant])

  /** Set open to false on breakpoint where burger menu is not needed */
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 1360) {
        setOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <>
      <header
        className={cn({
          'sticky w-full top-0 z-[100] py-2 md:py-4 pl-2 md:pl-4 xl:pl-12 2xl:pl-16':
            variant === 'sticky' && !fixedFadeIn,
          'fixed w-full top-0 z-[100] py-2 md:py-4 pl-2 md:pl-4 xl:pl-12 2xl:pl-16 transition-transform duration-200 ease-in-out [&:has(*:focus-visible)]:translate-y-0':
            variant === 'sticky' && fixedFadeIn,
          'nxl:-translate-y-full': hidden && fixedFadeIn,
          '-translate-y-full nxl:translate-y-0': hidden && !fixedFadeIn,
          'hidden nxl:block relative z-[100]': variant === 'static',
          'text-surface-solid translate-y-0': open,
        })}
      >
        <nav
          className={cn(
            'items-center p-4 md:pr-8',
            {
              'grid grid-cols-[1fr,auto] nxl:gap-8 nxl:grid-cols-[auto,minmax(max-content,1fr),auto]':
                variant === 'sticky',
              'flex justify-between md:px-8': variant === 'static',
            },
            // Background
            'backdrop-blur-lg bg-[#fff6]',
            'transition-[background-color,backdrop-filter] duration-300 ease-in-out',
          )}
        >
          <MenuLinks
            links={links}
            variant='desktop'
            className={cn('hidden nxl:block', {
              'nxl:block': variant === 'static',
            })}
            setOpen={setOpen}
          />
          {variant === 'sticky' && <HomeLink locale={locale} className='nxl:-order-1' />}
          <div className='flex gap-6 items-center h-full'>
            <AuthLinks
              registerLink={registerLink}
              logInLink={logInLink}
              variant='desktop'
              className='hidden md:block'
            />
            {variant === 'sticky' && (
              <BurgerButton
                onClick={() => setOpen(!open)}
                open={open}
                className='nxl:hidden'
                id={menuButtonId}
                aria-expanded={open}
                aria-controls={menuPaneId}
              />
            )}
          </div>

          {/* Mobile menu panel */}
          {variant === 'sticky' && (
            <div
              className={cn(
                'absolute -top-2 md:-top-4 right-0 w-screen h-dvh -z-10 overflow-hidden',
                {
                  'pointer-events-none': !open,
                },
              )}
            >
              <div
                className={cn(
                  'absolute top-0 left-full size-full bg-surface-solid theme-brand-1',
                  'transition-transform duration-500 ease-in-out',
                  {
                    '-translate-x-full': open,
                  },
                )}
                id={menuPaneId}
                aria-hidden={!open}
                role='menu'
                aria-labelledby={menuButtonId}
              >
                <Container className='!pt-36 h-full flex flex-col justify-between'>
                  <MenuLinks
                    links={links}
                    variant='mobile'
                    className='nxl:hidden'
                    tabIndex={!open ? -1 : undefined}
                    open={open}
                    setOpen={setOpen}
                    useMenuItem
                  />
                  <AuthLinks
                    registerLink={registerLink}
                    logInLink={logInLink}
                    variant='mobile'
                    tabIndex={!open ? -1 : undefined}
                    className='md:hidden'
                    useMenuItem
                  />
                </Container>
              </div>
            </div>
          )}
        </nav>
      </header>
    </>
  )
}

type HomeLinkProps = {
  locale: Locale
  className?: string
}

function HomeLink({ locale, className }: HomeLinkProps) {
  return (
    <div className={cn('flex items-center', className)}>
      <Link
        href={`/${locale === i18n.defaultLocale ? '' : locale}`}
        className={'inline-block h-8'}
      >
        <span className='sr-only'>Epico</span>
        <Logo
          className='h-full w-auto transition-colors duration-300 ease-in-out md:text-text'
          aria-hidden
        />
      </Link>
    </div>
  )
}

type MenuLinksProps = {
  links: NavItem[]
  variant: 'mobile' | 'desktop'
  open?: boolean
  tabIndex?: number
  className?: string
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  useMenuItem?: boolean
}

function MenuLinks({
  links,
  variant,
  open,
  tabIndex,
  className,
  setOpen,
  useMenuItem,
}: MenuLinksProps) {
  const [openDropdown, setOpenDropdown] = useState<number | null>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleMouseEnter = (index: number) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setOpenDropdown(index)
  }

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null)
    }, 150)
  }

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Escape') {
      setOpenDropdown(null)
      // Return focus to the parent link
      const parentLink = e.currentTarget.querySelector('a') as HTMLElement
      parentLink?.focus()
    }
    if (e.key === ' ') {
      e.preventDefault()
      setOpenDropdown(openDropdown === index ? null : index)
    }
  }

  return (
    <div className={className}>
      <ul
        className={cn('[&:has(>li>a:hover)>li>a]:opacity-40', {
          // 'grid gap-5': variant === 'mobile',
          'flex gap-6': variant === 'desktop',
        })}
      >
        {links?.map((link, index) => {
          const { href, title, prefetch, target } = link
          if (!href) return
          const hasChildren = link.children && link.children.length > 0
          const isDesktopDropdown = variant === 'desktop' && hasChildren
          const isMobileAccordion = variant === 'mobile' && hasChildren
          const isExpanded = openDropdown === index

          return (
            <li
              key={index}
              className={cn('relative', {
                'text-center transition-[transform,opacity] duration-500 ease-in-out':
                  variant === 'mobile',
                'translate-x-10 opacity-0': !open && variant === 'mobile',
              })}
              style={variant === 'mobile' ? { transitionDelay: `${(index + 1) * 0.1}s` } : undefined}
              onMouseEnter={isDesktopDropdown ? () => handleMouseEnter(index) : undefined}
              onMouseLeave={isDesktopDropdown ? handleMouseLeave : undefined}
              onKeyDown={isDesktopDropdown ? (e) => handleKeyDown(e, index) : undefined}
              onBlur={
                isDesktopDropdown
                  ? (e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        setOpenDropdown(null)
                      }
                    }
                  : undefined
              }
            >
              {isMobileAccordion ? (
                <div className='flex items-center justify-center'>
                  <BaseLink
                    href={href}
                    className='group hover:!opacity-100 transition-opacity duration-200 text-2xl font-bold py-3 inline-block'
                    prefetch={prefetch}
                    target={target}
                    tabIndex={tabIndex}
                    onClick={() => {
                      setOpen(false)
                    }}
                    role={useMenuItem ? 'menuitem' : undefined}
                  >
                    <span>{title}</span>
                  </BaseLink>
                  <button
                    type='button'
                    className='ml-2 p-2 transition-transform duration-200'
                    style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    tabIndex={tabIndex}
                    onClick={() => setOpenDropdown(isExpanded ? null : index)}
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title} submenu`}
                  >
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <BaseLink
                  href={href}
                  className={cn('group hover:!opacity-100 transition-opacity duration-200', {
                    'text-2xl font-bold py-3 inline-block': variant === 'mobile',
                    'text-sm font-bold py-1 relative': variant === 'desktop',
                  })}
                  prefetch={prefetch}
                  target={target}
                  tabIndex={tabIndex}
                  onClick={() => {
                    setOpen(false)
                  }}
                  role={useMenuItem ? 'menuitem' : undefined}
                  aria-haspopup={isDesktopDropdown ? 'true' : undefined}
                  aria-expanded={isDesktopDropdown ? isExpanded : undefined}
                >
                  <span>
                    {title}
                    {isDesktopDropdown && (
                      <svg
                        className='inline-block ml-1 w-3 h-3 transition-transform duration-200'
                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        viewBox='0 0 16 16'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        aria-hidden='true'
                      >
                        <path
                          d='M4 6L8 10L12 6'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </span>
                  {variant === 'desktop' && (
                    <span
                      className='block absolute theme-brand-2 bottom-0 -left-[0.75rem] w-[calc(100%+1.5rem)] h-[1.5px] bg-surface-solid opacity-0 group-hover:opacity-100 group-hover:duration-200 transition-opacity duration-500 ease-in-out outline-none'
                      aria-hidden
                    />
                  )}
                </BaseLink>
              )}

              {/* Desktop dropdown */}
              {isDesktopDropdown && isExpanded && (
                <ul
                  role='menu'
                  className='absolute top-full left-0 min-w-48 py-2 bg-surface-solid shadow-lg rounded-md'
                >
                  {link.children?.map((child, childIndex) => {
                    if (!child.href) return null
                    return (
                      <li key={childIndex} role='none'>
                        <BaseLink
                          href={child.href}
                          className='block px-4 py-2 text-sm hover:bg-black/5 transition-colors duration-150'
                          prefetch={child.prefetch}
                          target={child.target}
                          tabIndex={tabIndex}
                          onClick={() => {
                            setOpen(false)
                            setOpenDropdown(null)
                          }}
                          role='menuitem'
                        >
                          {child.title}
                        </BaseLink>
                      </li>
                    )
                  })}
                </ul>
              )}

              {/* Mobile accordion */}
              {isMobileAccordion && (
                <div
                  className='overflow-hidden transition-[max-height] duration-300 ease-in-out'
                  style={{ maxHeight: isExpanded ? `${(link.children?.length ?? 0) * 3}rem` : '0' }}
                >
                  <ul className='pb-2'>
                    {link.children?.map((child, childIndex) => {
                      if (!child.href) return null
                      return (
                        <li key={childIndex} className='text-center'>
                          <BaseLink
                            href={child.href}
                            className='block py-2 text-lg font-normal transition-opacity duration-200 hover:opacity-70'
                            prefetch={child.prefetch}
                            target={child.target}
                            tabIndex={tabIndex}
                            onClick={() => {
                              setOpen(false)
                              setOpenDropdown(null)
                            }}
                            role={useMenuItem ? 'menuitem' : undefined}
                          >
                            {child.title}
                          </BaseLink>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

type AuthLinksProps = {
  registerLink?: BaseLinkObject
  logInLink?: BaseLinkObject
  variant: 'mobile' | 'desktop'
  tabIndex?: number
  className?: string
  useMenuItem?: boolean
}

function AuthLinks({
  registerLink,
  logInLink,
  variant,
  tabIndex,
  className,
  useMenuItem,
}: AuthLinksProps) {
  return (
    <div className={className}>
      <div
        className={cn({
          'flex flex-col gap-4 max-w-md w-full mx-auto': variant === 'mobile',
          'flex gap-4 items-center': variant === 'desktop',
        })}
      >
        {registerLink?.href && (
          <BaseLink
            className={cn('transition-colors duration-300 ease-in-out', {
              'text-center font-bold': variant === 'mobile',
              'text-sm font-bold': variant === 'desktop',
            })}
            href={registerLink.href}
            prefetch={registerLink.prefetch}
            target={registerLink.target}
            tabIndex={tabIndex}
            role={useMenuItem ? 'menuitem' : undefined}
          >
            {registerLink.title}
          </BaseLink>
        )}
        {logInLink?.href && (
          <Button
            href={logInLink.href}
            className={cn({ 'flex justify-center items-center': variant === 'mobile' })}
            tabIndex={tabIndex}
            role={useMenuItem ? 'menuitem' : undefined}
            target='_blank'
          >
            {logInLink.title}
          </Button>
        )}
      </div>
    </div>
  )
}
